import React, { useState } from "react";
import { Link, Navigate, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { NRSLogo, NRSWordmark } from "../components/NRSLogo";
import { useT, LangToggle } from "../lib/i18n";
import { getPostAuthTarget } from "../lib/postAuthNavigation";

export default function Register() {
  const { user, register } = useAuth();
  const location = useLocation();
  const nav = useNavigate();
  const t = useT();
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  const signedInPath = (account) => getPostAuthTarget(account, location.state?.from);

  if (user) return <Navigate to={signedInPath(user)} replace />;

  function set(key, value) {
    setForm((current) => ({ ...current, [key]: value }));
  }

  async function submit(event) {
    event.preventDefault();
    setLoading(true);
    setErr("");
    const result = await register(form);
    setLoading(false);
    if (!result.ok) {
      setErr(result.error);
      return;
    }
    nav(signedInPath(result.user), { replace: true });
  }

  return (
    <main className="min-h-screen flex items-center justify-center p-4 sm:p-8 bg-[#070B14]">
      <form onSubmit={submit} className="w-full max-w-lg space-y-5 grid-card p-5 sm:p-8" data-testid="register-form">
        <div className="flex items-center gap-3 border-b border-[var(--border)] pb-4">
          <NRSLogo size={34} />
          <div className="min-w-0">
            <NRSWordmark className="text-sm" />
            <p className="mt-1 text-xs text-[var(--text-2)]">European Medical Device Regulatory Platform</p>
          </div>
        </div>
        <div className="flex items-start justify-between gap-4">
          <div>
            <h1 className="text-3xl font-extrabold tracking-tight">{t("register.title")}</h1>
            <p className="text-sm text-[var(--text-2)] mt-2">{t("register.demo_intro")}</p>
          </div>
          <LangToggle />
        </div>
        <label className="block">
          <span className="text-xs font-semibold tracking-wide uppercase text-[var(--text-2)]">{t("register.name_optional")}</span>
          <input
            data-testid="reg-name"
            type="text"
            autoComplete="name"
            value={form.name}
            onChange={(event) => set("name", event.target.value)}
            className="mt-1 w-full border border-[var(--border)] px-3 py-2 rounded-sm bg-[var(--surface)] text-[var(--text)] focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500/20 focus:outline-none"
          />
        </label>
        <label className="block">
          <span className="text-xs font-semibold tracking-wide uppercase text-[var(--text-2)]">{t("login.email")}</span>
          <input
            data-testid="reg-email"
            type="email"
            autoComplete="email"
            required
            value={form.email}
            onChange={(event) => set("email", event.target.value)}
            className="mt-1 w-full border border-[var(--border)] px-3 py-2 rounded-sm bg-[var(--surface)] text-[var(--text)] focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500/20 focus:outline-none"
          />
        </label>
        <label className="block">
          <span className="text-xs font-semibold tracking-wide uppercase text-[var(--text-2)]">{t("login.password")}</span>
          <input
            data-testid="reg-password"
            type="password"
            autoComplete="new-password"
            minLength={6}
            required
            value={form.password}
            onChange={(event) => set("password", event.target.value)}
            className="mt-1 w-full border border-[var(--border)] px-3 py-2 rounded-sm bg-[var(--surface)] text-[var(--text)] focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500/20 focus:outline-none"
          />
          <span className="mt-1 block text-xs text-[var(--text-2)]">{t("register.password_minimum")}</span>
        </label>
        <div className="rounded-sm border border-cyan-800/60 bg-cyan-950/30 p-4 text-sm leading-relaxed text-[var(--text-2)]">
          <strong className="block text-[var(--text)]">{t("register.demo_title")}</strong>
          <span>{t("register.demo_limits")}</span>
        </div>
        {err && <div data-testid="register-error" role="alert" className="text-sm text-red-400 border border-red-500/30 bg-red-950/40 px-3 py-2">{err}</div>}
        <button data-testid="register-submit" disabled={loading} className="btn-primary w-full py-2.5">
          {loading ? t("register.submitting") : t("register.demo_cta")}
        </button>
        <div className="text-sm text-[var(--text-2)]">
          {t("register.has_account")} <Link className="text-cyan-400 hover:underline font-semibold" to="/login" state={location.state} data-testid="register-to-login">{t("register.login")}</Link>
        </div>
      </form>
    </main>
  );
}
